// Homepage top: a working board that an agent clears while you watch, an app dock that
// rides the bottom of the screen until you reach it, and photo-led "areas of work" tabs.
// Photos in /img are from Unsplash (free to use under the Unsplash licence).
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, LayoutGroup, motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, CheckSquare, ChevronDown, Sparkles, User, Zap } from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrations } from "@/data/site";

const isPrerender = () => typeof navigator !== "undefined" && /HeadlessChrome/.test(navigator.userAgent);
const EASE = [0.22, 1, 0.36, 1] as const;

/* ── Hero board ───────────────────────────────────────────────────────────── */
type Col = "blocked" | "progress" | "review" | "done";
const COLS: { key: Col; label: string; chip: string }[] = [
  { key: "blocked", label: "Blocked", chip: "bg-[hsl(0_0%_94%)] text-[hsl(0_0%_25%)] border-[hsl(0_0%_78%)]" },
  { key: "progress", label: "In progress", chip: "bg-[hsl(216_90%_95%)] text-[hsl(216_84%_38%)] border-[hsl(216_84%_62%)]" },
  { key: "review", label: "Ready for review", chip: "bg-background text-foreground border-foreground" },
  { key: "done", label: "Done", chip: "bg-[hsl(216_84%_52%)] text-background border-[hsl(216_84%_52%)]" },
];
// people on the board stay in the site's colours: ink, blue, deep blue, grey
const WHO = ["bg-[hsl(0_0%_15%)]", "bg-[hsl(216_84%_52%)]", "bg-[hsl(216_60%_28%)]", "bg-[hsl(0_0%_55%)]"];
interface Card { id: string; title: string; ref: string; col: Col; who: number; agent?: boolean }
const START: Card[] = [
  { id: "a", title: "Renew supplier contract", ref: "OPS-50", col: "blocked", who: 0 },
  { id: "b", title: "Answer partner pricing question", ref: "OPS-60", col: "blocked", who: -1 },
  { id: "c", title: "Confirm onboarding call times", ref: "OPS-40", col: "blocked", who: 2 },
  { id: "d", title: "Chase unpaid March invoice", ref: "OPS-55", col: "blocked", who: -1 },
  { id: "e", title: "Review weekly feedback", ref: "OPS-80", col: "progress", who: 1 },
  { id: "f", title: "Document reporting needs", ref: "OPS-70", col: "progress", who: 2 },
  { id: "g", title: "Update the price list", ref: "OPS-65", col: "progress", who: 3 },
  { id: "h", title: "Draft rollout plan", ref: "OPS-30", col: "review", who: 2 },
  { id: "i", title: "Map open customer requests", ref: "OPS-20", col: "review", who: 0 },
];
// each beat: which card moves where (and whether an agent picked it up)
const BEATS: [string, Col, boolean][] = [
  ["b", "progress", true], ["d", "progress", true], ["h", "done", false], ["e", "review", false],
  ["b", "review", true], ["i", "done", false], ["d", "done", true], ["c", "progress", true],
];
const PROMPT = "Let's clear what is blocked before Friday";

export const HeroBoard = ({ bare = false }: { bare?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const reduce = useReducedMotion();
  const still = reduce || isPrerender();
  const [cards, setCards] = useState<Card[]>(START);
  const [typed, setTyped] = useState(still ? PROMPT.length : 0);
  const [beat, setBeat] = useState(0);

  useEffect(() => {
    if (still || !inView) return;
    if (typed < PROMPT.length) {
      const t = setTimeout(() => setTyped((n) => n + 1), 55);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      if (beat >= BEATS.length) {
        setCards(START); setBeat(0); setTyped(0);
        return;
      }
      const [id, col, agent] = BEATS[beat];
      setCards((cs) => cs.map((c) => (c.id === id ? { ...c, col, agent: agent || c.agent } : c)));
      setBeat((b) => b + 1);
    }, beat >= BEATS.length ? 2600 : beat === 0 ? 700 : 1300);
    return () => clearTimeout(t);
  }, [still, inView, typed, beat]);

  return (
    <div ref={ref} aria-hidden className={`relative mx-auto w-full ${bare ? "pb-12" : "max-w-[1180px] pb-10"}`}>
      <LayoutGroup id="hero-board">
        <div className={`grid grid-cols-2 gap-3 rounded-xl bg-[hsl(40_9%_97%)] p-3 text-left sm:gap-4 lg:grid-cols-4 ${bare ? "sm:p-3.5" : "border border-border shadow-[0_1px_0_hsl(0_0%_100%)_inset,0_30px_60px_-40px_hsl(0_0%_0%/0.35)] sm:p-5"}`}>
          {COLS.map((col, ci) => (
            <div key={col.key} className={`min-h-[210px] ${bare ? "sm:min-h-[340px]" : "sm:min-h-[400px]"} ${ci > 1 ? "hidden lg:block" : ""}`}>
              <div className="mb-3 flex items-center justify-between px-1">
                <span className={`rounded border px-1.5 py-0.5 text-[13px] font-medium ${col.chip}`}>{col.label}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-2.5">
                {cards.filter((c) => c.col === col.key).map((c) => (
                  <motion.div
                    key={c.id}
                    layoutId={`hb-${c.id}`}
                    transition={{ type: "spring", stiffness: 260, damping: 30 }}
                    className="rounded-md border border-[hsl(0_0%_86%)] bg-background p-3 shadow-[0_1px_1px_hsl(0_0%_0%/0.06)]"
                  >
                    <p className="text-[13px] font-medium leading-snug text-foreground sm:text-[14.5px]">{c.title}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                        {c.agent ? <Zap className="h-3.5 w-3.5 text-[hsl(216_84%_52%)]" /> : <CheckSquare className="h-3.5 w-3.5 text-[hsl(0_0%_45%)]" />}
                        {c.ref}
                      </span>
                      {c.agent ? (
                        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 18 }} className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(216_84%_52%)] text-background">
                          <Sparkles className="h-3.5 w-3.5" />
                        </motion.span>
                      ) : c.who < 0 ? (
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(0_0%_88%)] text-[hsl(0_0%_35%)]"><User className="h-3.5 w-3.5" /></span>
                      ) : (
                        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-white ${WHO[c.who]}`}><User className="h-3.5 w-3.5" /></span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </LayoutGroup>
      {/* the request that sets it all moving */}
      <div className="absolute bottom-0 left-1/2 flex w-[min(92%,460px)] -translate-x-1/2 items-center gap-3 rounded-xl border border-border bg-background px-5 py-4 text-left shadow-[0_18px_40px_-18px_hsl(0_0%_0%/0.35)]">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background"><User className="h-4 w-4" /></span>
        <span className="min-w-0 flex-1 truncate text-[16px] font-medium text-foreground sm:text-[19px]">
          {PROMPT.slice(0, typed)}
          <span className="cm-caret ml-px inline-block h-[1.05em] w-[2px] translate-y-[0.18em] bg-foreground" />
        </span>
      </div>
    </div>
  );
};

/* ── Integration dock: rides the bottom edge until its own place scrolls into view ── */
// The apps ClearMakes Inbox connects to (from data/site.ts, some with their own page),
// then more apps from the app catalogue. The strip runs the full width,
// slowly and fades at both edges; hovering pauses it.
const COMPOSIO_APPS: { slug: string; name: string }[] = [
  { slug: "salesforce", name: "Salesforce" },
  { slug: "googledrive", name: "Google Drive" },
  { slug: "jira", name: "Jira" }, { slug: "github", name: "GitHub" },
  { slug: "googlesheets", name: "Google Sheets" }, { slug: "zendesk", name: "Zendesk" },
  { slug: "shopify", name: "Shopify" }, { slug: "linear", name: "Linear" },
  { slug: "asana", name: "Asana" }, { slug: "trello", name: "Trello" },
  { slug: "airtable", name: "Airtable" }, { slug: "intercom", name: "Intercom" },
  { slug: "stripe", name: "Stripe" }, { slug: "calendly", name: "Calendly" },
  { slug: "discord", name: "Discord" }, { slug: "dropbox", name: "Dropbox" },
  { slug: "zoom", name: "Zoom" }, { slug: "clickup", name: "ClickUp" },
];

const dockItem = "group flex shrink-0 items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-[15px] font-medium text-foreground transition-colors hover:bg-[hsl(40_9%_94%)]";
const dockLogo = "h-6 w-6 object-contain opacity-80 grayscale transition-[filter,opacity] duration-300 group-hover:opacity-100 group-hover:grayscale-0";

const DockApps = () => (
  <>
    {integrations.map((i) => {
      const inner = (
        <>
          <img src={i.logo} alt={`${i.name} logo`} className={dockLogo} />
          <span className="whitespace-nowrap">{i.name}</span>
        </>
      );
      return i.href ? (
        <SmartLink key={i.key} href={i.href} className={dockItem}>{inner}</SmartLink>
      ) : (
        <span key={i.key} className={dockItem}>{inner}</span>
      );
    })}
    {COMPOSIO_APPS.map((a) => (
      <SmartLink key={a.slug} href="https://agents.clearmakes.com/#apps" className={dockItem}>
        <img src={`https://logos.composio.dev/api/${a.slug}`} alt={`${a.name} logo`} referrerPolicy="no-referrer" className={dockLogo} />
        <span className="whitespace-nowrap">{a.name}</span>
      </SmartLink>
    ))}
    <SmartLink href="/integrations" className={`${dockItem} font-semibold text-[hsl(216_84%_45%)]`}>
      <span className="whitespace-nowrap">And thousands more apps</span>
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
    </SmartLink>
  </>
);

export const AppDock = () => (
  <nav aria-label="Tools ClearMakes products connect to" className="sticky bottom-3 z-30 mx-auto mt-10 w-[calc(100%-16px)] sm:w-[calc(100%-32px)] lg:w-[calc(100%-80px)]">
    {/* The pill stays solid; only the sliding row inside fades at its edges. */}
    <div className="overflow-hidden rounded-2xl border border-border bg-background p-1.5 shadow-[0_20px_50px_-20px_hsl(0_0%_0%/0.4)]">
      <div className="cm-marquee overflow-hidden">
        <div className="cm-marquee-track flex w-max items-center gap-1" style={{ ["--cm-marquee-s" as string]: "70s" }}>
          <DockApps />
          {/* the second copy makes the loop seamless; screen readers skip it */}
          <div aria-hidden className="flex items-center gap-1">
            <DockApps />
          </div>
        </div>
      </div>
    </div>
  </nav>
);

/* ── Areas of work: tabs with a framed photograph ─────────────────────────── */
interface Area {
  key: string; tab: string; title: string; body: string; color: string; img: string; alt: string; pill: string; status: string;
  includes: { label: string; href: string }[]; href: string;
}
const AREAS: Area[] = [
  {
    key: "communication", tab: "Communication", color: "hsl(216 84% 52%)", img: "/img/team-laptops.jpg", alt: "A small team working on laptops around a shared table",
    pill: "Workflow", status: "DONE",
    title: "Routine messages, handled by workflows you build.",
    body: "Connect the apps you use and let workflows act on new messages. When an email arrives, a workflow can summarise it, draft a reply or pass it to the right person.",
    includes: [{ label: "ClearMakes Inbox", href: "/products/inbox" }, { label: "Workflows", href: "/workflows" }], href: "/products/inbox",
  },
  {
    key: "coordination", tab: "Coordination", color: "hsl(32 90% 50%)", img: "/img/team-whiteboard.jpg", alt: "A team planning with sticky notes on a wall",
    pill: "Apps", status: "CONNECTED",
    title: "Your tools stay in step, with no copying and pasting.",
    body: "Connect Gmail, Outlook, Slack, Teams, Notion, HubSpot and more. A workflow can post to Slack, add a note in HubSpot or create a Notion page when something new comes in.",
    includes: [{ label: "Apps", href: "/integrations" }, { label: "Workflows", href: "/workflows" }], href: "/integrations",
  },
  {
    key: "knowledge", tab: "Knowledge", color: "hsl(150 55% 36%)", img: "/img/team-meeting.jpg", alt: "Two colleagues discussing work in front of a laptop",
    pill: "Search", status: "FOUND",
    title: "The answer your team already gave, found in seconds.",
    body: "Decisions get buried in threads and documents. Agents find the answer, cite where it came from and close the question.",
    includes: [{ label: "ClearMakes Agents", href: "https://agents.clearmakes.com" }], href: "https://agents.clearmakes.com",
  },
  {
    key: "questions", tab: "Questions", color: "hsl(285 55% 52%)", img: "/img/team-highfive.jpg", alt: "Two colleagues celebrating at a desk",
    pill: "Smart FAQ", status: "ANSWERED",
    title: "The questions you get every day, answered for you.",
    body: "Write your answers once. Smart FAQ uses them to answer the same questions when they come in again, so you stop typing the same reply.",
    includes: [{ label: "Smart FAQ", href: "/smart-faq" }], href: "/smart-faq",
  },
];

const Framed = ({ a }: { a: Area }) => (
  <div className="relative mx-auto w-full max-w-[560px] pb-10 pr-2">
    <motion.div
      className="absolute inset-x-0 bottom-16 top-0"
      style={{ background: a.color, clipPath: "polygon(0 12%, 100% 0, 100% 88%, 0 96%)" }}
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }}
    />
    <motion.img
      src={a.img} alt={a.alt} width={1100} height={760} loading="lazy"
      className="relative ml-[7%] mt-[10%] block aspect-[5/3.4] w-[86%] object-cover grayscale"
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08, ease: EASE }}
    />
    <motion.span
      className="absolute left-[10%] top-[20%] rounded-full border-2 bg-[hsl(0_0%_9%)] px-4 py-1.5 text-[18px] font-medium text-white"
      style={{ borderColor: a.color }}
      initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, type: "spring", stiffness: 300, damping: 18 }}
    >
      {a.pill}
    </motion.span>
    <motion.span
      className="absolute bottom-[19%] right-[8%] rounded px-2 py-0.5 text-[13px] font-bold tracking-[0.04em] text-white"
      style={{ background: a.color }}
      initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.75 }}
    >
      {a.status}
    </motion.span>
    <svg aria-hidden viewBox="0 0 300 110" className="absolute bottom-0 left-[14%] w-[58%] overflow-visible">
      <motion.path
        d="M2 104 C 60 60, 110 40, 140 52 C 170 64, 120 96, 160 86 C 210 74, 250 40, 292 8"
        fill="none" stroke="hsl(0 0% 9%)" strokeWidth="2.2" strokeLinecap="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, delay: 0.3, ease: "easeInOut" }}
      />
      <motion.path d="M276 8 L293 7 L290 24" fill="none" stroke="hsl(0 0% 9%)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15 }} />
    </svg>
  </div>
);

export const WorkAreas = () => {
  const [key, setKey] = useState(AREAS[0].key);
  const a = AREAS.find((x) => x.key === key)!;
  return (
    <div>
      <LayoutGroup id="work-areas">
        <div className="cm-noscroll -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div role="tablist" aria-label="Areas of work" className="mx-auto flex w-max gap-1 rounded-full bg-[hsl(40_9%_95%)] p-1.5">
            {AREAS.map((t) => (
              <button key={t.key} role="tab" type="button" aria-selected={t.key === key} onClick={() => setKey(t.key)}
                className={`relative rounded-full px-5 py-2.5 text-[16px] font-semibold transition-colors duration-200 sm:px-8 ${t.key === key ? "text-white" : "text-foreground hover:bg-[hsl(40_9%_90%)]"}`}>
                {t.key === key && <motion.span layoutId="work-pill" className="absolute inset-0 rounded-full bg-[hsl(0_0%_9%)]" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <span className="relative whitespace-nowrap">{t.tab}</span>
              </button>
            ))}
          </div>
        </div>
      </LayoutGroup>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={a.key} role="tabpanel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
          className="mt-16 grid items-center gap-14 lg:grid-cols-[1.25fr_1fr] lg:gap-10">
          <div>
            <p className="text-[1.6rem] tracking-[-0.02em] text-foreground"><span className="font-extrabold">{a.tab}</span> work</p>
            <h3 className="mt-7 max-w-2xl text-[2.2rem] font-medium leading-[1.12] tracking-[-0.03em] text-foreground sm:text-[3rem]">{a.title}</h3>
            <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-foreground/80 sm:text-[18px]">{a.body}</p>
            <p className="mt-12 text-[14px] font-semibold uppercase tracking-[0.06em] text-foreground">Available today</p>
            <ul className="mt-5 flex flex-wrap gap-x-8 gap-y-4">
              {a.includes.map((i) => (
                <li key={i.label}>
                  <SmartLink href={i.href} className="group inline-flex items-center gap-3 text-[22px] text-foreground">
                    <span className="flex h-10 w-10 items-center justify-center rounded-[10px] text-white transition-transform duration-200 group-hover:-translate-y-0.5" style={{ background: a.color }}>
                      <Sparkles className="h-5 w-5" />
                    </span>
                    {i.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
            <SmartLink href={a.href} className="group mt-12 inline-flex items-center gap-1.5 text-[16px] font-medium text-[hsl(216_84%_45%)]">
              Explore {a.tab.toLowerCase()} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </SmartLink>
          </div>
          <Framed a={a} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
